import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  type SelfRatedSupply,
  type SettlementTrigger,
  useSaveSelfRatedSupply,
  useSelfRatedSupply,
} from "@/api/self-rated-supply";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  carrierId: string;
  carrierName: string;
}

function errorMessage(error: unknown) {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data.error ?? "Unable to save supply setup.";
  }
  return "Unable to save supply setup.";
}

export function SelfRatedSupplySection(props: Props) {
  const query = useSelfRatedSupply(props.carrierId);
  return (
    <section
      aria-label="Self-rated supply setup"
      className="space-y-3 rounded-lg border p-4"
    >
      <h3 className="font-medium">Self-rated supply setup</h3>
      <p className="text-sm text-muted-foreground">
        Use this when {props.carrierName} prices its services using our rate
        tables. EZX pays {props.carrierName} under the terms you select below.
      </p>
      {query.isPending ? (
        <p role="status">Loading supply setup…</p>
      ) : query.isError ? (
        <div role="alert">
          <p>Unable to load supply setup. {errorMessage(query.error)}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => void query.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <SupplyForm
          key={`${props.carrierId}:${query.data?.updatedAt ?? "new"}`}
          {...props}
          supply={query.data}
          reload={() => void query.refetch()}
        />
      )}
    </section>
  );
}

function SupplyForm({
  carrierId,
  supply,
  reload,
}: Props & {
  supply: SelfRatedSupply | null;
  reload: () => void;
}) {
  const mutation = useSaveSelfRatedSupply(carrierId);
  const [active, setActive] = useState(supply?.active ?? true);
  const [priority, setPriority] = useState(String(supply?.priority ?? 100));
  const [currency, setCurrency] = useState(supply?.terms?.currency ?? "");
  const [trigger, setTrigger] = useState<SettlementTrigger | "">(
    supply?.terms?.settlementTrigger ?? ""
  );
  const [error, setError] = useState<string | null>(null);
  const incompatibleTerms =
    supply?.terms &&
    (supply.terms.payeeType !== "CARRIER" ||
      supply.terms.payeeId !== carrierId ||
      supply.terms.payerType !== "EZX");

  async function save() {
    const numericPriority = Number(priority);
    if (
      !trigger ||
      !/^[A-Z]{3}$/.test(currency) ||
      !Number.isInteger(numericPriority) ||
      numericPriority <= 0
    ) {
      setError(
        "Choose settlement timing, a three-letter currency code, and a positive whole-number priority."
      );
      return;
    }
    setError(null);
    try {
      await mutation.mutateAsync({
        expectedUpdatedAt: supply?.updatedAt ?? null,
        active,
        priority: numericPriority,
        currency,
        settlementTrigger: trigger,
      });
      toast.success(
        "Supply setup saved. Continue configuring services, coverage and rates."
      );
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  if (incompatibleTerms) {
    return (
      <p role="alert">
        This channel has different settlement parties and cannot be changed with
        this editor.
      </p>
    );
  }

  return (
    <fieldset disabled={mutation.isPending} className="space-y-3">
      <p className="text-sm">
        {supply?.terms
          ? supply.active
            ? "Supply enabled"
            : "Supply disabled"
          : "Supply setup incomplete"}
        . Services, coverage, rates and operational setup are still required.
      </p>
      <div className="flex items-center gap-2">
        <input
          id="supply-active"
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
        <Label htmlFor="supply-active">Enable self-rated supply</Label>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="supply-trigger">When EZX owes the carrier</Label>
          <select
            id="supply-trigger"
            className="h-10 w-full rounded-md border bg-background px-3 text-sm"
            value={trigger}
            onChange={(e) => {
              const value = e.target.value;
              if (
                value === "" ||
                value === "AT_PURCHASE" ||
                value === "ON_COMPLETION" ||
                value === "ON_INVOICE"
              )
                setTrigger(value);
            }}
          >
            <option value="">Choose settlement timing</option>
            <option value="AT_PURCHASE">At purchase</option>
            <option value="ON_COMPLETION">On completion</option>
            <option value="ON_INVOICE">On invoice</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="supply-currency">Settlement currency</Label>
          <Input
            id="supply-currency"
            placeholder="e.g. CAD"
            maxLength={3}
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="supply-priority">Supply priority</Label>
          <Input
            id="supply-priority"
            type="number"
            min={1}
            step={1}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Higher priority is preferred when other supply channels exist.
          </p>
        </div>
      </div>
      {!!supply?.serviceIds.length && (
        <p className="text-sm text-muted-foreground">
          Existing service restrictions are preserved.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Changed terms apply from this save. Previously recorded terms remain
        attached to existing quotes and shipments.
      </p>
      {error && (
        <div role="alert">
          <p>{error}</p>
          <Button type="button" variant="outline" onClick={reload}>
            Reload saved supply setup
          </Button>
        </div>
      )}
      <Button type="button" onClick={() => void save()}>
        {mutation.isPending ? "Saving supply…" : "Save supply setup"}
      </Button>
    </fieldset>
  );
}
